import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { useRegion } from '../auth/RegionContext';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { JustifiedAction } from '../actions/JustifiedAction';
import { formatMoney } from '../cashbacks/formatMoney';
import { ResolveLostAction } from './ResolveLostAction';
import type { DisputeRow } from './chargebacksModel';
import {
  CHARGEBACK_REASON_LABEL,
  DISPUTE_SOURCE_LABEL,
  DISPUTE_STATUS_COLOR,
  DISPUTE_STATUS_LABEL,
} from './chargebacksState';

/**
 * One disputed transaction in the chargebacks worklist (D-chargebacks): the order,
 * the dispute source and reason, the settlement status (which STAYS confirmed), the
 * member's true signed balance, and the lifecycle actions where the role permits.
 *
 * Trust is ASYMMETRIC BY OUTCOME: the fraud reviewer works the dispute and resolves
 * WON (value-neutral, chargebacks:review); resolving LOST claws back value and is
 * super_admin-only (chargebacks:resolve_loss, via ResolveLostAction). A lost
 * resolution writes a reversal ledger entry, never a direct balance write.
 */
export const ChargebackRow = ({
  row,
  now,
}: {
  row: DisputeRow;
  now: number;
}) => {
  const { accessibleRegions } = useRegion();
  const { role } = useAccessContext();
  const ctx = { role, accessibleRegions };
  const can = (capability: Parameters<typeof hasAccess>[1]['capability']) =>
    hasAccess(ctx, { capability });

  const nowIso = new Date(now).toISOString();
  const open =
    row.disputeStatus === 'received' || row.disputeStatus === 'under_review';
  const prevForResolve = {
    id: row.id,
    disputeStatus: row.disputeStatus,
    resolvedAt: null,
  };

  return (
    <Card
      variant="outlined"
      data-testid={`chargeback-row-${row.id}`}
      sx={{ mb: 2 }}
    >
      <CardContent>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {row.memberName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.market.toUpperCase()} | order{' '}
              {formatMoney(row.grossAmount, row.currency)} | cashback{' '}
              {formatMoney(row.cashbackAmount, row.currency)}
            </Typography>
          </Box>
          <Chip
            size="small"
            variant="outlined"
            color={DISPUTE_STATUS_COLOR[row.disputeStatus]}
            label={DISPUTE_STATUS_LABEL[row.disputeStatus]}
          />
          {/* The settlement status stays confirmed throughout the dispute. */}
          <Chip
            size="small"
            variant="outlined"
            label={`Order ${row.status}`}
            data-testid={`chargeback-settlement-${row.id}`}
          />
          {row.disputeSource && (
            <Typography variant="caption" color="text.secondary">
              from {DISPUTE_SOURCE_LABEL[row.disputeSource]}
            </Typography>
          )}
          {row.disputeReasonCode && (
            <Chip
              size="small"
              variant="outlined"
              label={CHARGEBACK_REASON_LABEL[row.disputeReasonCode]}
            />
          )}
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ mt: 1 }}
        >
          Member balance:{' '}
          <Box
            component="span"
            data-testid={`chargeback-balance-${row.id}`}
            sx={{
              fontWeight: 600,
              color:
                row.memberBalanceSigned < 0 ? 'error.main' : 'text.secondary',
            }}
          >
            {formatMoney(row.memberBalanceSigned, row.currency)}
          </Box>
        </Typography>

        {row.disputeNote && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ mt: 0.5 }}
          >
            Note: {row.disputeNote}
          </Typography>
        )}

        {row.disputeStatus === 'lost' && (
          <Typography
            variant="caption"
            color="error.main"
            component="div"
            sx={{ mt: 0.5 }}
            data-testid={`chargeback-reversal-${row.id}`}
          >
            Clawed back {formatMoney(row.reversalAmount, row.currency)}
          </Typography>
        )}

        {open && (
          <Stack
            direction="row"
            spacing={2}
            sx={{ mt: 2 }}
            useFlexGap
            flexWrap="wrap"
          >
            {row.disputeStatus === 'received' && can('chargebacks:review') && (
              <JustifiedAction
                resource="transactions"
                capability="chargebacks:review"
                id={row.id}
                label="Start review"
                color="primary"
                dialogTitle="Move to under review"
                dialogIntro="Begin working this dispute. Record the evidence note."
                buildData={(note) => ({
                  disputeStatus: 'under_review',
                  disputeNote: note,
                })}
                previousData={{ id: row.id, disputeStatus: 'received' }}
                successMessage="Dispute under review"
              />
            )}
            {can('chargebacks:review') && (
              <JustifiedAction
                resource="transactions"
                capability="chargebacks:review"
                id={row.id}
                label="Resolve won"
                color="success"
                dialogTitle="Resolve this dispute won"
                dialogIntro="The dispute is resolved in the platform's favour. The cashback is kept, no clawback. Record why."
                buildData={(note) => ({
                  disputeStatus: 'won',
                  resolvedAt: nowIso,
                  disputeSource: row.disputeSource,
                  disputeReasonCode: row.disputeReasonCode,
                  disputeNote: note,
                })}
                previousData={prevForResolve}
                successMessage="Dispute resolved won"
              />
            )}
            {can('chargebacks:resolve_loss') ? (
              <ResolveLostAction row={row} now={now} />
            ) : (
              <Typography variant="caption" color="text.secondary">
                Resolving a dispute lost is a super_admin only reversal.
              </Typography>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
